import { useMemo, useRef, useState } from "react";
import { Alert, Button, DataTable, Dialog, Field } from "../../../ui";
import type { DataColumn, FieldElement } from "../../../ui";
import { failure } from "../../workspace/behavior";
import type { Schemas } from "../../workspace/api.generated";
import { businessCodeLabel, date, money } from "../../workspace/model";
import { FilterBar, PageHeader, Panel, RowLink, SectionTabs, statusBadge } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import type { ReconciliationMutation } from "../queries";
import { reconciliationRunFormSchema } from "../schema";
import type { IssueStatusFilter, ReconciliationRunFormValues } from "../schema";

type QueryState = "ready" | "loading" | "error";
export type ReconciliationListTab = "runs" | "issues";

function todayInKorea(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function providerLabel(row: Schemas["ProviderConfigurationView"]): string {
  return `${row.configurationName} · ${row.providerCode}/${businessCodeLabel(row.serviceType)} · ${businessCodeLabel(row.mode)} · ID ${row.id}`;
}

export interface ReconciliationListViewProps {
  readonly tab: ReconciliationListTab;
  readonly issueStatus: IssueStatusFilter;
  readonly runs?: readonly Schemas["ReconciliationRunView"][];
  readonly runsState: QueryState;
  readonly issues?: readonly Schemas["ReconciliationIssueView"][];
  readonly issuesState: QueryState;
  readonly providers?: Schemas["ProviderConfigurationPage"];
  readonly providersState: "idle" | QueryState;
  readonly canManage: boolean;
  readonly notice?: string;
  readonly runHref: (runId: number) => string;
  readonly issueHref: (runId: number, issueId: number) => string;
  readonly onTabChange: (tab: ReconciliationListTab) => void;
  readonly onIssueStatusChange: (status: IssueStatusFilter) => void;
  readonly onOpenRun: (run: Schemas["ReconciliationRunView"]) => void;
  readonly onOpenIssue: (issue: Schemas["ReconciliationIssueView"]) => void;
  readonly onRetryRuns: () => void;
  readonly onRetryIssues: () => void;
  readonly onRetryProviders: () => void;
  readonly onMutation: (input: ReconciliationMutation) => Promise<unknown>;
  readonly onRunComplete: (run: Schemas["ReconciliationRunView"]) => void;
}

export function ReconciliationListView(props: ReconciliationListViewProps) {
  const [statusDraft, setStatusDraft] = useState(props.issueStatus);
  const [runForm, setRunForm] = useState<ReconciliationRunFormValues>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState("");
  const [pending, setPending] = useState(false);
  const refs = useRef<Record<string, FieldElement | null>>({});

  const runColumns = useMemo(() => [
    {
      key: "number",
      title: "대사번호",
      sticky: "start" as const,
      render: (row: Schemas["ReconciliationRunView"]) => (
        <RowLink
          href={props.runHref(row.id)}
          triggerKey={`reconciliation-run:${row.id}:detail`}
          onNavigate={() => props.onOpenRun(row)}
        >
          {row.runNo}
        </RowLink>
      ),
    },
    { key: "provider", title: "Provider 설정 ID", numeric: true, render: (row: Schemas["ReconciliationRunView"]) => row.providerConfigurationId },
    { key: "type", title: "유형", render: (row: Schemas["ReconciliationRunView"]) => businessCodeLabel(row.reconciliationType) },
    { key: "date", title: "기준일", render: (row: Schemas["ReconciliationRunView"]) => row.businessDate },
    { key: "checked", title: "확인", numeric: true, render: (row: Schemas["ReconciliationRunView"]) => row.checkedCount },
    { key: "matched", title: "일치", numeric: true, render: (row: Schemas["ReconciliationRunView"]) => row.matchedCount },
    { key: "issues", title: "불일치", numeric: true, render: (row: Schemas["ReconciliationRunView"]) => row.issueCount },
    { key: "status", title: "상태", render: (row: Schemas["ReconciliationRunView"]) => statusBadge(row.status) },
    { key: "started", title: "시작일시", render: (row: Schemas["ReconciliationRunView"]) => date(row.startedAt) },
  ] satisfies readonly DataColumn<Schemas["ReconciliationRunView"]>[], [props]);

  const issueColumns = useMemo(() => [
    {
      key: "id",
      title: "이슈 ID",
      sticky: "start" as const,
      render: (row: Schemas["ReconciliationIssueView"]) => (
        <RowLink
          href={props.issueHref(row.reconciliationRunId, row.id)}
          triggerKey={`reconciliation-issue:${row.id}:detail`}
          onNavigate={() => props.onOpenIssue(row)}
        >
          {row.id}
        </RowLink>
      ),
    },
    { key: "run", title: "소속 실행 ID", numeric: true, render: (row: Schemas["ReconciliationIssueView"]) => row.reconciliationRunId },
    { key: "type", title: "불일치 유형", render: (row: Schemas["ReconciliationIssueView"]) => businessCodeLabel(row.issueType) },
    { key: "ref", title: "업무 대상", render: (row: Schemas["ReconciliationIssueView"]) => row.businessRefType ? `${businessCodeLabel(row.businessRefType)} ${row.businessRefId ?? "—"}` : "—" },
    { key: "internal", title: "내부 금액", numeric: true, render: (row: Schemas["ReconciliationIssueView"]) => money(row.internalAmount) },
    { key: "provider", title: "Provider 금액", numeric: true, render: (row: Schemas["ReconciliationIssueView"]) => money(row.providerAmount) },
    { key: "status", title: "상태", render: (row: Schemas["ReconciliationIssueView"]) => statusBadge(row.status) },
  ] satisfies readonly DataColumn<Schemas["ReconciliationIssueView"]>[], [props]);

  const openRunDialog = () => {
    const firstProvider = props.providers?.items[0];
    setErrors({});
    setRequestError("");
    setRunForm({
      providerConfigurationId: firstProvider ? String(firstProvider.id) : "",
      type: "ALL",
      businessDate: todayInKorea(),
    });
  };

  const submitRun = async () => {
    if (!runForm || pending) return;
    const parsed = reconciliationRunFormSchema.safeParse(runForm);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setErrors(next);
      refs.current[String(parsed.error.issues[0]?.path[0])]?.focus();
      return;
    }
    setPending(true);
    setRequestError("");
    try {
      const result = await props.onMutation({
        kind: "run",
        body: {
          providerConfigurationId: Number(parsed.data.providerConfigurationId),
          type: parsed.data.type,
          businessDate: parsed.data.businessDate,
        },
      }) as Schemas["ReconciliationRunView"];
      setRunForm(undefined);
      props.onRunComplete(result);
    } catch (cause) {
      setRequestError(failure(cause).message);
    } finally {
      setPending(false);
    }
  };

  const canOpenRun = props.canManage
    && props.providersState === "ready"
    && Boolean(props.providers?.items.length);

  return <>
    <PageHeader
      id="ADM-S18"
      title="대사 관리"
      description="대사 실행과 전체 실행에서 발견된 최근 불일치 이슈를 구분해 확인합니다."
      meta={<p aria-live="polite">{props.tab === "runs" ? `최근 실행 ${props.runs?.length ?? 0}건` : `현재 조건 이슈 ${props.issues?.length ?? 0}건`}</p>}
      action={<GuidedActionButton size="small" blockedReason={canOpenRun ? null : !props.canManage ? "대사 실행 권한이 없습니다. 권한 관리자에게 요청해 주세요." : props.providersState === "loading" ? "연동 설정을 불러오는 중입니다. 조회가 끝난 뒤 실행해 주세요." : props.providersState === "error" ? "연동 설정 조회에 실패했습니다. 다시 시도를 눌러 조회해 주세요." : "활성 연동 설정이 없습니다. 연동 설정에서 연결 테스트와 활성화를 완료해 주세요."} onClick={openRunDialog}>대사 실행</GuidedActionButton>}
    />
    {props.notice ? <Alert persistent variant="information" title="대사 실행 결과">{props.notice}</Alert> : null}
    {!props.canManage ? <Alert persistent variant="information" title="조회 전용 권한">대사 실행과 이슈 해결은 PROVIDER_MANAGE 권한이 필요합니다.</Alert> : null}
    {props.canManage && props.providersState === "error" ? <Alert persistent variant="danger" title="Provider 설정을 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={props.onRetryProviders}>다시 시도</Button>}>기존 대사 이력은 계속 확인할 수 있습니다.</Alert> : null}
    {props.canManage && props.providersState === "ready" && !props.providers?.items.length ? <Alert persistent variant="information" title="활성 Provider 설정이 없습니다">연동 설정에서 연결 테스트와 활성화를 완료한 뒤 대사를 실행해 주세요.</Alert> : null}

    <SectionTabs
      items={["실행", "전체 이슈"]}
      value={props.tab === "runs" ? "실행" : "전체 이슈"}
      onChange={(value) => props.onTabChange(value === "실행" ? "runs" : "issues")}
    />

    {props.tab === "runs" ? (
      <Panel title="대사 실행 이력" description="실행번호를 선택하면 해당 실행의 집계와 그 실행에 속한 이슈만 확인할 수 있습니다.">
        <DataTable caption="대사 실행 이력" columns={runColumns} rows={props.runs ?? []} rowKey={(row) => row.id} state={props.runsState} onRetry={props.onRetryRuns} />
      </Panel>
    ) : <>
      <Panel title="전체 실행의 최근 이슈" description="최근 조회 범위의 이슈입니다. 소속 실행 ID를 함께 확인하세요.">
        <FilterBar onSubmit={() => props.onIssueStatusChange(statusDraft)}>
          <Field compact control="select" label="이슈 상태" value={statusDraft} onChange={(event) => setStatusDraft(event.currentTarget.value as IssueStatusFilter)}>
            <option value="">전체</option>
            <option value="OPEN">미해결</option>
            <option value="RESOLVED">해결</option>
          </Field>
        </FilterBar>
        <DataTable caption="전체 실행의 최근 대사 이슈" columns={issueColumns} rows={props.issues ?? []} rowKey={(row) => row.id} state={props.issuesState} onRetry={props.onRetryIssues} />
      </Panel>
    </>}

    <Dialog
      open={Boolean(runForm)}
      size="medium"
      title="대사를 실행할까요?"
      description="Provider 설정, 유형과 기준일을 확인한 뒤 한 번만 실행합니다."
      closeDisabled={pending}
      onClose={() => { if (!pending) setRunForm(undefined); }}
      secondaryAction={<Button variant="quiet" disabled={pending} onClick={() => setRunForm(undefined)}>취소</Button>}
      primaryAction={<GuidedActionButton isLoading={pending} blockedReason={props.canManage ? null : "대사 실행 권한이 없습니다. 권한 관리자에게 요청해 주세요."} onClick={() => void submitRun()}>대사 실행 요청</GuidedActionButton>}
    >
      {requestError ? <Alert persistent variant="danger" title="대사를 실행하지 못했습니다">{requestError} 입력값을 유지했습니다.</Alert> : null}
      {runForm ? <form className="admin-command" noValidate onSubmit={(event) => { event.preventDefault(); void submitRun(); }}>
        <Field ref={(element) => { refs.current.providerConfigurationId = element; }} compact control="select" label="Provider 설정" required value={runForm.providerConfigurationId} error={errors.providerConfigurationId} onChange={(event) => { setRunForm({ ...runForm, providerConfigurationId: event.currentTarget.value }); setErrors((current) => ({ ...current, providerConfigurationId: "" })); }}>
          <option value="">선택</option>
          {(props.providers?.items ?? []).map((provider) => <option key={provider.id} value={provider.id}>{providerLabel(provider)}</option>)}
        </Field>
        <Field compact control="select" label="대사 유형" required value={runForm.type} onChange={(event) => setRunForm({ ...runForm, type: event.currentTarget.value as ReconciliationRunFormValues["type"] })}>
          <option value="ALL">전체</option><option value="PAYMENT">결제</option><option value="TRANSFER">송금</option><option value="ORPHAN">고아 참조</option>
        </Field>
        <Field ref={(element) => { refs.current.businessDate = element; }} compact label="기준일" required type="date" value={runForm.businessDate} error={errors.businessDate} onChange={(event) => { setRunForm({ ...runForm, businessDate: event.currentTarget.value }); setErrors((current) => ({ ...current, businessDate: "" })); }} />
      </form> : null}
    </Dialog>
  </>;
}
